// The transport seam (P0.11) — Architecture §11's "/telemetry — transport
// (isolation-compatible)".
//
// "Isolation-compatible" is doing real work in that sentence. ADR-3 makes the page cross-origin
// isolated, which means COEP require-corp, which means **no third-party script tags**. A telemetry
// vendor's usual `<script src="https://cdn.vendor.com/...">` snippet is not an option here — it
// would be blocked, and the CSP from P0.9 would block it again for good measure. Stack §9 knew
// this: "SDK bundled first-party, so COEP-safe; event submission is a plain fetch."
//
// The interface exists because OQ-S1 is not settled. Stack §11 leaves it open — PostHog EU versus
// a first-party Cloudflare Worker — and calls it "a stance, not a technicality". PostHog is the
// plan's recommendation and the current choice, with "revisit at Pro tier" attached. A seam is how
// that revisit stays cheap: the first-party endpoint becomes another implementation of this file,
// and nothing above it changes.

import type { TelemetryEvent } from './events';

export interface TelemetryTransport {
  /** Send one event. Fire-and-forget: telemetry must never make the app wait. */
  send(event: TelemetryEvent): void;
  /** Report an error. Scrubbed by the implementation, per FR-44. */
  captureError(error: unknown): void;
  /** Best-effort flush, for beforeunload. */
  flush(): Promise<void>;
}

/**
 * Where PostHog EU lives. Named here rather than inline because P0.9's CSP has to allow it, and
 * a hostname that appears in exactly one place cannot drift out of sync with the header that
 * permits it. `security-headers.ts` imports this.
 *
 * EU, not US, and not a detail: FR-44 requires telemetry "compliant with EU requirements", and
 * Stack §9's recommendation is specifically "PostHog (EU cloud)". The US host would be a
 * different product decision wearing the same SDK.
 */
export const POSTHOG_HOST = 'https://eu.i.posthog.com';

/**
 * Where Sentry ingests. Also needed by the CSP.
 *
 * **This is org-specific, and that is the whole difficulty.** A Sentry DSN embeds the organisation
 * and the region — `https://<key>@o<org>.ingest.de.sentry.io/<project>` — so the host the app must
 * be allowed to reach is not a Sentry-wide constant, it is *this account's*. P0.11 wrote
 * `https://o0.ingest.sentry.io` as a placeholder because no account existed; `o0` is not a real
 * org, so the CSP has been naming a host that cannot answer.
 *
 * `.de.` is Germany: the EU region, chosen to match FR-44 and the deliberate EU choice for PostHog.
 * Sentry's region is fixed when the organisation is created and is not migratable, so this letter
 * pair is a decision that would cost a new org to revisit.
 *
 * **If the DSN ever changes, this must change with it**, or the browser will block every report and
 * the error tracker will fail in the one way an error tracker must not: silently, with the console
 * carrying the only clue. `assertDsnMatchesCsp` below is what turns that into a loud failure.
 */
export const SENTRY_INGEST_ORIGIN = 'https://o4511750579814400.ingest.de.sentry.io';

/**
 * Does this DSN point where the CSP allows?
 *
 * Cheap insurance against a genuinely nasty failure. The DSN lives in an environment variable and
 * `SENTRY_INGEST_ORIGIN` lives in source; nothing links them, so a new DSN silently disagrees with
 * the CSP and every report is blocked by the browser. No exception is thrown, no test goes red, and
 * errors simply stop arriving — which nobody notices, because the absence of error reports looks
 * exactly like the absence of errors.
 *
 * Returns the problem as a string rather than throwing: telemetry may never be the reason the app
 * fails to start.
 */
export function dsnCspMismatch(dsn: string): string | null {
  let origin: string;
  try {
    origin = new URL(dsn).origin;
  } catch {
    return `Sentry DSN is not a URL: ${dsn}`;
  }

  return origin === SENTRY_INGEST_ORIGIN
    ? null
    : `Sentry DSN points at ${origin} but the CSP only permits ${SENTRY_INGEST_ORIGIN}. ` +
        `Every report would be blocked by the browser, silently. Update SENTRY_INGEST_ORIGIN in ` +
        `src/telemetry/transport.ts — the CSP is generated from it.`;
}

/**
 * Configuration, read from the environment at build time.
 *
 * Both values are *public by design* — they ship in the client bundle and identify a project, not
 * a person or an account. They are not secrets, and treating them as secrets would be theatre.
 * What they are is **absent**: no accounts exist yet (deferred alongside the Cloudflare deploy),
 * so telemetry is inert in every environment today. That is a state the code has to handle
 * honestly rather than crash on.
 */
export interface TelemetryConfig {
  readonly posthogKey: string | null;
  readonly sentryDsn: string | null;
}

/**
 * An env var that is present but empty is **absent**, not configured.
 *
 * `?? null` was here until P0.12 and only caught `undefined`, which meant an empty string was a
 * perfectly good key: `isConfigured` said yes, the 101 kB transport chunk was fetched, and
 * `posthog.init('')` ran. Nothing threw. The app would look fine and quietly ship an SDK to every
 * visitor for the privilege of talking to nobody.
 *
 * Not hypothetical, and not a "someone might one day" — it is what CI does **by default**. GitHub
 * Actions substitutes an *unset* `${{ vars.X }}` with the empty string, never with undefined. So the
 * moment the build started receiving these vars (P0.12), every build without them configured —
 * every fork, every outside PR — would have taken the configured path.
 *
 * Trimmed too, because a trailing newline pasted out of a dashboard is the other way this arrives.
 */
function readKey(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

export function readConfig(): TelemetryConfig {
  return {
    // Dot access works because src/vite-env.d.ts declares these. Vite's own typing is an index
    // signature returning `any`, which would let a typo'd name compile and be undefined
    // forever — and `any` on the path that decides whether telemetry loads is the last place
    // to want it.
    posthogKey: readKey(import.meta.env.VITE_POSTHOG_KEY),
    sentryDsn: readKey(import.meta.env.VITE_SENTRY_DSN),
  };
}

/** True when there is somewhere to send. Without it, nothing is loaded and nothing is sent. */
export function isConfigured(config: TelemetryConfig): boolean {
  return config.posthogKey !== null || config.sentryDsn !== null;
}
